import { Fragment, useState } from "react";
import { api } from "@/lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageHeader, LoadingState, EmptyState, ErrorState } from "@/components/features/states";
import { formatRelativeTime } from "@/lib/format";
import { toast } from "sonner";
import { Flag, Check, X, ShieldAlert, ChevronDown, ChevronRight } from "lucide-react";
import type { FraudFlag } from "@/lib/types";
import { useTranslation } from "react-i18next";

const STATUS_TABS = [
  { key: "open", labelKey: "admin.fraudFlags.tabsOpen" },
  { key: "reviewing", labelKey: "admin.fraudFlags.tabsReviewing" },
  { key: "resolved", labelKey: "admin.fraudFlags.tabsResolved" },
  { key: "dismissed", labelKey: "admin.fraudFlags.tabsDismissed" },
  { key: "all", labelKey: "admin.fraudFlags.tabsAll" },
];

function severityBadge(sev: FraudFlag["severity"]) {
  const map: Record<string, string> = {
    high: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
    medium: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    low: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${map[sev] ?? map.low}`}>
      {sev}
    </span>
  );
}

function statusBadge(status: FraudFlag["status"]) {
  const map: Record<string, string> = {
    open: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
    reviewing: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    resolved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    dismissed: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${map[status] ?? ""}`}>
      {status}
    </span>
  );
}

export function AdminFraudFlags() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [tab, setTab] = useState("open");
  const [resolving, setResolving] = useState<FraudFlag | null>(null);
  const [resolution, setResolution] = useState("");
  const [action, setAction] = useState<"resolved" | "dismissed">("resolved");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin", "fraud-flags"],
    queryFn: () => api.admin.fraudFlags(),
    refetchInterval: 20000,
  });
  const all = data?.data?.flags ?? [];
  const flags = tab === "all" ? all : all.filter((f) => f.status === tab);

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openResolve = (flag: FraudFlag, initialAction: "resolved" | "dismissed" = "resolved") => {
    setResolving(flag);
    setResolution("");
    setAction(initialAction);
  };

  const doResolve = async () => {
    if (!resolving) return;
    if (!resolution.trim()) return toast.error(t("admin.fraudFlags.enterResolutionNote"));
    setSaving(true);
    const res = await api.admin.resolveFraudFlag(resolving.id, {
      status: action,
      resolution: resolution.trim(),
    });
    setSaving(false);
    if (!res.ok) return toast.error(res.error || t("admin.fraudFlags.resolveFailed"));
    toast.success(action === "resolved" ? t("admin.fraudFlags.flagResolvedToast") : t("admin.fraudFlags.flagDismissedToast"));
    setResolving(null);
    setResolution("");
    qc.invalidateQueries({ queryKey: ["admin", "fraud-flags"] });
    qc.invalidateQueries({ queryKey: ["admin", "stats"] });
    qc.invalidateQueries({ queryKey: ["admin", "analytics"] });
  };

  if (isLoading) return <LoadingState />;
  if (error || !data?.ok) return <ErrorState message={data?.error || t("admin.fraudFlags.loadFailed")} onRetry={refetch} />;

  const counts: Record<string, number> = { open: 0, reviewing: 0, resolved: 0, dismissed: 0, all: all.length };
  all.forEach((f) => {
    counts[f.status] = (counts[f.status] || 0) + 1;
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("admin.fraudFlags.title")}
        subtitle={t("admin.fraudFlags.subtitle", { open: counts.open, reviewing: counts.reviewing, resolved: counts.resolved })}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full justify-start overflow-x-auto">
          {STATUS_TABS.map((s) => (
            <TabsTrigger key={s.key} value={s.key} className="flex-1">
              {t(s.labelKey)}
              <Badge variant="secondary" className="ml-1.5">
                {counts[s.key] ?? 0}
              </Badge>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {flags.length === 0 ? (
        <EmptyState
          icon={<ShieldAlert className="h-8 w-8" />}
          title={t("admin.fraudFlags.emptyTitle")}
          description={tab === "all" ? t("admin.fraudFlags.emptyDescAll") : t("admin.fraudFlags.emptyDesc", { status: t(`admin.fraudFlags.tabs${tab.charAt(0).toUpperCase()}${tab.slice(1)}`) })}
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>{t("admin.fraudFlags.colUser")}</TableHead>
                <TableHead>{t("admin.fraudFlags.colRule")}</TableHead>
                <TableHead>{t("admin.fraudFlags.colSeverity")}</TableHead>
                <TableHead>{t("admin.fraudFlags.colStatus")}</TableHead>
                <TableHead>{t("admin.fraudFlags.colRaised")}</TableHead>
                <TableHead className="text-right">{t("admin.fraudFlags.colActions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {flags.map((flag) => {
                const isOpen = expanded.has(flag.id);
                return (
                  <Fragment key={flag.id}>
                    <TableRow className="cursor-pointer" onClick={() => toggleExpand(flag.id)}>
                      <TableCell className="text-muted-foreground">
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-7 w-7">
                            <AvatarFallback className="bg-primary/10 text-[10px] font-bold text-primary">
                              {(flag.user?.name || "?").slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium">{flag.user?.name || t("admin.fraudFlags.unknownUser")}</p>
                            <p className="text-[10px] text-muted-foreground">
                              {t("admin.fraudFlags.fraudScoreLabel")} <span className="font-mono">{flag.user?.fraudScore ?? 0}</span>
                              {flag.user?.flagged && <span className="ml-1 text-red-600">· {t("admin.fraudFlags.flaggedLabel")}</span>}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="rounded bg-muted px-1.5 py-0.5 text-[11px]">{flag.rule}</code>
                      </TableCell>
                      <TableCell>{severityBadge(flag.severity)}</TableCell>
                      <TableCell>{statusBadge(flag.status)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatRelativeTime(flag.createdAt)}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {(flag.status === "open" || flag.status === "reviewing") ? (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8"
                                onClick={() => openResolve(flag, "resolved")}
                              >
                                <Check className="mr-1 h-3.5 w-3.5" /> {t("admin.fraudFlags.resolveButton")}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 text-muted-foreground"
                                onClick={() => openResolve(flag, "dismissed")}
                              >
                                <X className="mr-1 h-3.5 w-3.5" /> {t("admin.fraudFlags.dismissButton")}
                              </Button>
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground">{flag.resolution || "—"}</span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow>
                        <TableCell colSpan={7} className="bg-muted/30">
                          <div className="space-y-2 p-2 text-xs">
                            <p>
                              <span className="font-medium">{t("admin.fraudFlags.descriptionLabel")} </span>
                              <span className="text-muted-foreground">{flag.description}</span>
                            </p>
                            {flag.metadata && (
                              <div>
                                <p className="font-medium">{t("admin.fraudFlags.metadataLabel")}</p>
                                <pre className="mt-1 max-h-40 overflow-auto rounded bg-background p-2 text-[10px]">
                                  {(() => {
                                    try {
                                      return JSON.stringify(JSON.parse(flag.metadata), null, 2);
                                    } catch {
                                      return flag.metadata;
                                    }
                                  })()}
                                </pre>
                              </div>
                            )}
                            {flag.resolvedAt && (
                              <p className="text-muted-foreground">
                                {t("admin.fraudFlags.resolvedAgo", { time: formatRelativeTime(flag.resolvedAt), resolution: flag.resolution })}
                              </p>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Resolve / dismiss dialog */}
      <Dialog open={!!resolving} onOpenChange={(o) => !o && setResolving(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="h-4 w-4 text-red-600" />
              {action === "resolved" ? t("admin.fraudFlags.resolveFlagTitle") : t("admin.fraudFlags.dismissFlagTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {resolving && (
              <div className="rounded-lg bg-muted/40 p-3 text-xs">
                <p><span className="text-muted-foreground">{t("admin.fraudFlags.dialogUser")}</span> <span className="font-medium">{resolving.user?.name}</span></p>
                <p><span className="text-muted-foreground">{t("admin.fraudFlags.dialogRule")}</span> <code>{resolving.rule}</code></p>
                <p><span className="text-muted-foreground">{t("admin.fraudFlags.dialogSeverity")}</span> {resolving.severity}</p>
                <p className="mt-1"><span className="text-muted-foreground">{t("admin.fraudFlags.dialogDescription")}</span> {resolving.description}</p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>{t("admin.fraudFlags.outcomeLabel")}</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setAction("resolved")}
                  className={`flex items-center gap-1.5 rounded-lg border p-2 text-sm ${action === "resolved" ? "border-primary bg-primary/5" : "border-border"}`}
                >
                  <Check className="h-4 w-4" /> {t("admin.fraudFlags.resolveButton")}
                </button>
                <button
                  onClick={() => setAction("dismissed")}
                  className={`flex items-center gap-1.5 rounded-lg border p-2 text-sm ${action === "dismissed" ? "border-primary bg-primary/5" : "border-border"}`}
                >
                  <X className="h-4 w-4" /> {t("admin.fraudFlags.dismissButton")}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="res">{t("admin.fraudFlags.resolutionNoteLabel")}</Label>
              <Textarea
                id="res"
                rows={3}
                placeholder={
                  action === "resolved"
                    ? t("admin.fraudFlags.resolutionPlaceholderResolved")
                    : t("admin.fraudFlags.resolutionPlaceholderDismissed")
                }
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolving(null)}>{t("admin.fraudFlags.cancel")}</Button>
            <Button
              variant={action === "resolved" ? "default" : "secondary"}
              onClick={doResolve}
              disabled={saving || !resolution.trim()}
            >
              {saving ? t("admin.fraudFlags.saving") : action === "resolved" ? t("admin.fraudFlags.resolveButton") : t("admin.fraudFlags.dismissButton")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
